import type { Difficulty, MCQQuestion } from './types';

function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

type QType = 'find_area' | 'find_side' | 'compare_area' | 'unit_concept';

/**
 * N-3-14: rectangle area in square centimetres.
 * difficulty 1: length x width = area (small integers)
 * difficulty 2: find missing side given area; includes squares
 * difficulty 3: compare areas of two rectangles
 */
export function generateAreaRectangle(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  const qTypes: QType[] = difficulty === 1
    ? ['find_area', 'unit_concept']
    : difficulty === 2
    ? ['find_area', 'find_side']
    : ['find_side', 'compare_area', 'find_area'];

  const qType = qTypes[randInt(rand, 0, qTypes.length - 1)]!;

  let prompt: string;
  let correct: string;
  let options: string[];
  let explanation: string;

  if (qType === 'find_area') {
    const maxEdge = difficulty === 1 ? 9 : 15;
    const l = randInt(rand, 2, maxEdge);
    const w = randInt(rand, 2, maxEdge);
    const area = l * w;
    correct = area + ' 平方公分';
    prompt = '長方形的長是 ' + l + ' 公分，寬是 ' + w + ' 公分，面積是多少平方公分？';
    const w1 = (area + l) + ' 平方公分';
    const w2 = (area + w) + ' 平方公分';
    const w3 = ((l + w) * 2) + ' 平方公分';
    options = [correct, w1, w2, w3];
    explanation = '面積 = 長 × 寬 = ' + l + ' × ' + w + ' = ' + area + ' 平方公分。';

  } else if (qType === 'find_side') {
    const knownSide = randInt(rand, 2, 10);
    const unknownSide = randInt(rand, 2, 10);
    const area = knownSide * unknownSide;
    const askFor = rand() > 0.5 ? '長' : '寬';
    correct = unknownSide + ' 公分';
    prompt = '長方形面積是 ' + area + ' 平方公分，' + (askFor === '長' ? '寬' : '長') + '是 ' + knownSide + ' 公分，' + askFor + '是多少公分？';
    const w1 = (unknownSide + 1) + ' 公分';
    const w2 = (unknownSide - 1 > 0 ? unknownSide - 1 : unknownSide + 2) + ' 公分';
    const w3 = (area - knownSide) + ' 公分';
    options = [correct, w1, w2, w3];
    explanation = askFor + ' = 面積 ÷ ' + (askFor === '長' ? '寬' : '長') + ' = ' + area + ' ÷ ' + knownSide + ' = ' + unknownSide + ' 公分。';

  } else if (qType === 'compare_area') {
    const l1 = randInt(rand, 3, 10);
    const w1r = randInt(rand, 3, 10);
    const area1 = l1 * w1r;
    let l2 = randInt(rand, 2, 12);
    let w2r = randInt(rand, 2, 12);
    while (l2 * w2r === area1) l2 = randInt(rand, 2, 12);
    const area2 = l2 * w2r;
    const largerLabel = area1 > area2 ? '甲' : '乙';
    correct = largerLabel;
    prompt = '甲長方形長 ' + l1 + ' 公分、寬 ' + w1r + ' 公分；乙長方形長 ' + l2 + ' 公分、寬 ' + w2r + ' 公分。哪個面積比較大？';
    options = ['甲', '乙', '一樣大', '無法比較'];
    explanation = '甲面積 = ' + l1 + '×' + w1r + ' = ' + area1 + '；乙面積 = ' + l2 + '×' + w2r + ' = ' + area2 + '。' + (area1 > area2 ? '甲' : '乙') + ' 比較大。';

  } else {
    const side = randInt(rand, 2, 8);
    const area = side * side;
    correct = area + ' 平方公分';
    prompt = '邊長 ' + side + ' 公分的正方形，面積是多少平方公分？';
    const w1 = (4 * side) + ' 平方公分';
    const w2 = (area + side) + ' 平方公分';
    const w3 = side + ' 平方公分';
    options = [correct, w1, w2, w3];
    explanation = '正方形面積 = 邊長 × 邊長 = ' + side + ' × ' + side + ' = ' + area + ' 平方公分。';
  }

  const seen = new Set();
  const uniqueOpts: string[] = [];
  for (const o of options) {
    if (!seen.has(o)) { seen.add(o); uniqueOpts.push(o); }
  }
  while (uniqueOpts.length < 4) {
    const extra = randInt(rand, 4, 200) + ' 平方公分';
    if (!seen.has(extra) && extra !== correct) { seen.add(extra); uniqueOpts.push(extra); }
  }
  if (!uniqueOpts.includes(correct)) uniqueOpts[0] = correct;

  const shuffled = uniqueOpts.slice(0, 4).sort(() => rand() - 0.5);

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.area.rectangle',
  };
}
